import { useId } from "react";

import { ICONE_BARCO_ROTULO } from "../icone-barco";
import {
  OBRA_CARD_METRICAS,
  OBRA_CARD_REALIZADO_LABEL,
  OBRA_CARD_ROTULO_FORA_ATE,
  OBRA_CARD_SEM_VALOR,
  OBRA_CARD_TOOLTIP_PROPS,
} from "./obra-card.constants";
import type { ObraCardProps } from "./types-obra-card";

const numero = (casas: { min?: number; max: number }) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: casas.min ?? 0,
    maximumFractionDigits: casas.max,
  });
const MILHOES = numero({ min: 1, max: 1 });
const PERCENTUAL = numero({ max: 1 });

/** `yyyy-MM-dd` → `dd/MM/aa` (protótipo). */
function data(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${(match[1] ?? "").slice(2)}` : OBRA_CARD_SEM_VALOR;
}

const percentual = (valor: number | null) =>
  valor === null ? OBRA_CARD_SEM_VALOR : `${PERCENTUAL.format(valor)}%`;

const milhoes = (valor: number | null) =>
  valor === null ? OBRA_CARD_SEM_VALOR : `R$ ${MILHOES.format(valor)}M`;

const limitar = (valor: number) => Math.min(100, Math.max(0, valor));

/**
 * Percentual da barra (0 a 100): o que a tela mandar pronto (`null` = sem barra) ou o realizado sobre o orçado; sem os
 * dois valores, ou com orçado zero, não há barra.
 */
function progresso(
  realizado: number | null,
  orcado: number | null,
  pronto: number | null | undefined,
): number | null {
  if (pronto !== undefined) return pronto === null ? null : limitar(pronto);
  if (realizado === null || orcado === null || orcado <= 0) return null;
  return limitar((realizado / orcado) * 100);
}

/** ViewModel do `ObraCard`: textos formatados, barra do realizado, métricas, tooltips e testids; sem JSX. */
export function useObraCard({
  codigo,
  embarcacao,
  condicao,
  condicaoTooltip,
  inicio,
  fim,
  duracaoDias,
  tipos,
  realizado,
  orcado,
  realizadoPercentual,
  barraTooltip,
  metricas,
  testId,
  className,
  style,
  ...rest
}: ObraCardProps) {
  const escopo = useId();
  const percentualRealizado = progresso(realizado, orcado, realizadoPercentual);
  const tooltip = condicaoTooltip ?? (condicao ? ICONE_BARCO_ROTULO[condicao] : undefined);
  const part = (nome: string) => (testId ? `${testId}-${nome}` : undefined);

  return {
    rest,
    className,
    style,
    testId,
    codigo,
    embarcacao: embarcacao || OBRA_CARD_SEM_VALOR,
    icone: {
      condicao: condicao ?? ("molhado" as const),
      tooltip,
      tabIndex: tooltip ? 0 : undefined,
    },
    inicio: data(inicio),
    fim: data(fim),
    duracao: duracaoDias === null ? OBRA_CARD_SEM_VALOR : `${duracaoDias} d`,
    tipos,
    realizadoLabel: OBRA_CARD_REALIZADO_LABEL,
    realizado: `${milhoes(realizado)} / ${milhoes(orcado)}`,
    barra: {
      valor: percentualRealizado === null ? undefined : Math.round(percentualRealizado),
      rotulo: percentualRealizado === null ? undefined : percentual(percentualRealizado),
      preenchimento: { width: `${percentualRealizado ?? 0}%` },
      rotuloFora:
        percentualRealizado !== null && percentualRealizado < OBRA_CARD_ROTULO_FORA_ATE
          ? true
          : undefined,
      tooltip: barraTooltip,
      testId: part("realizado"),
    },
    metricas: OBRA_CARD_METRICAS.map((metrica) => ({
      key: metrica.key,
      sigla: metrica.sigla,
      nome: metrica.nome,
      valor: percentual(metricas[metrica.key]),
      testId: part(`indicador-${metrica.sigla}`),
    })),
    /** Uma tooltip por card, presa aos alvos `data-pr-tooltip` dele (os cards que chegam depois já nascem ligados). */
    escopo,
    tooltipTarget: `[data-obra-card="${escopo}"] [data-pr-tooltip]`,
    tooltipProps: OBRA_CARD_TOOLTIP_PROPS,
  };
}
